import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { programService } from '../../../services/programService';
import { parseUnitWorkbook, unitsToCSV } from '../../../utils/csvHelpers';
import { unitService } from '../../../services/unitService';
import { roomService } from '../../../services/roomService';

const UnitModal = ({ isOpen, onClose, onSave, unit }) => {
  // Wizard State
  const [step, setStep] = useState(1); // 1: Context, 2: Action, 3: Form/Bulk
  const [actionType, setActionType] = useState('single'); // 'single', 'bulk'

  // Context State
  const [context, setContext] = useState({
    programId: '',
    programLevel: '',
    module: 1
  });

  // Form State
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    programId: '',
    creditHours: 3,
    hoursPerWeek: 4,
    sessionLengthMinutes: 90,
    maxCapacity: 30,
    waitingListEnabled: false,
    isActive: true,
    module: 1,
    programLevel: '',
    unitType: 'Core',
    roomType: 'Standard Classroom',
    teachingGroupCode: '',
    allowCombinedDelivery: false,
    enrolled: 0
  });

  const [programs, setPrograms] = useState([]);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [pairedUnitIds, setPairedUnitIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [error, setError] = useState(null);

  // Bulk Upload State
  const fileInputRef = useRef(null);
  const [uploadStats, setUploadStats] = useState(null);

  // Reset wizard on open
  useEffect(() => {
    if (isOpen) {
      fetchPrograms();
      fetchAvailableUnits();
      roomService.getRoomTypes().then(setRoomTypes).catch(err => setError(err.message || 'Failed to load room types'));
      if (unit) {
        // Edit Mode: Skip wizard, populate form
        setStep(3);
        setActionType('single');
        setFormData({
          code: unit?.code || '',
          name: unit?.name || '',
          programId: unit?.programId || '',
          creditHours: unit?.creditHours || 3,
          hoursPerWeek: unit?.hoursPerWeek || 4,
          sessionLengthMinutes: unit?.sessionLengthMinutes || 90,
          waitingListEnabled: unit?.waitingListEnabled || false,
          isActive: unit?.isActive !== undefined ? unit?.isActive : true,
          module: unit?.module || 1,
          programLevel: unit?.programLevel || '',
          unitType: unit?.unitType || 'Core',
          roomType: unit?.roomType || 'Standard Classroom',
          teachingGroupCode: unit?.teachingGroupCode || '',
          allowCombinedDelivery: unit?.allowCombinedDelivery || false,
          enrolled: unit?.enrolled || 0,
          examTheoryHours: unit?.examTheoryHours || '',
          examPracticalHours: unit?.examPracticalHours || ''
        });
        setContext({
          programId: unit?.programId || '',
          programLevel: unit?.programLevel || '',
          module: unit?.module || 1
        });
      } else {
        // Create Mode: Start Wizard
        setStep(1);
        setActionType('single');
        // Reset Context & Form
        setContext({ programId: '', programLevel: '', module: 1 });
        setFormData({
          code: '',
          name: '',
          programId: '',
          creditHours: 3,
          hoursPerWeek: 4,
          sessionLengthMinutes: 90,
          waitingListEnabled: false,
          isActive: true,
          module: 1,
          programLevel: '',
          unitType: 'Core',
          roomType: 'Standard Classroom',
          teachingGroupCode: '',
          allowCombinedDelivery: false,
          enrolled: 0,
          examTheoryHours: '',
          examPracticalHours: ''
        });
      }
      setError(null);
      setUploadStats(null);
      if (!unit?.allowCombinedDelivery) setPairedUnitIds([]);
    }
  }, [isOpen, unit]);

  const fetchPrograms = async () => {
    try {
      setLoadingPrograms(true);
      const data = await programService?.getAll();
      setPrograms(data || []);
    } catch (err) {
      console.error('Error fetching programs:', err);
      setError('Failed to load programs');
    } finally {
      setLoadingPrograms(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked :
        type === 'number' ? (value ? parseInt(value, 10) : '') :
          value
    }));
  };

  const getMaxModules = (program, selectedLevel) => {
    if (!program || !program.levelDurations) return 99; // Fallback
    const levelNumber = String(selectedLevel || '').match(/[3-7]/)?.[0];
    const direct = program.levelDurations[levelNumber] ?? program.levelDurations[selectedLevel];
    if (direct) return parseInt(direct) || 1;
    const matchingKey = Object.keys(program.levelDurations).find(key => String(key).includes(levelNumber));
    return parseInt(program.levelDurations[matchingKey]) || 99;
  };

  const handleContextSubmit = (e) => {
    e?.preventDefault();
    if (!context.programId) {
      setError('Please select a program');
      return;
    }
    if (!context.programLevel) { setError('Please select the programme level'); return; }

    const selectedProgram = programs.find(p => p.id === context.programId);
    const maxModules = getMaxModules(selectedProgram, context.programLevel);

    if (context.module > maxModules) {
      setError(`Module ${context.module} exceeds the maximum (${maxModules}) for ${selectedProgram.name}`);
      return;
    }

    // Update form data with context
    setFormData(prev => ({
      ...prev,
      programId: context.programId,
      programLevel: Number(String(context.programLevel).match(/[3-7]/)?.[0]),
      module: context.module
    }));
    setError(null);
    setStep(2);
  }

  // Update programs whenever needed - programs state already has the data from getAll, 
  // ensuring we access levelDurations from it.

  const handleActionSelect = (type) => {
    setActionType(type);
    setStep(3);
  }

  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Validation
    if (!formData?.code?.trim()) {
      setError('Unit code is required');
      return;
    }
    if (!formData?.name?.trim()) {
      setError('Unit name is required');
      return;
    }
    if (!formData?.programId) {
      setError('Program selection is required');
      return;
    }
    if (formData.allowCombinedDelivery && !unit?.teachingGroupCode && pairedUnitIds.length === 0) {
      setError('Select at least one existing unit for this teaching group.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await onSave({ ...formData, pairedUnitIds });
      onClose();
    } catch (err) {
      console.error('Error saving unit:', err);
      setError(err?.message || 'Failed to save unit');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    const a = document.createElement('a');
    a.href = '/templates/unit_bulk_upload_template.xlsx';
    a.download = 'unit_bulk_upload_template.xlsx';
    a.click();
  };

  const fetchAvailableUnits = async () => {
    const data = await unitService.getAll();
    const candidates = (data || []).filter(candidate => candidate.id !== unit?.id);
    setAvailableUnits(candidates);
    if (unit?.allowCombinedDelivery && unit?.teachingGroupCode) {
      const existingPairs = candidates.filter(candidate => candidate.allowCombinedDelivery && candidate.teachingGroupCode === unit.teachingGroupCode);
      setPairedUnitIds(existingPairs.map(candidate => candidate.id));
    }
  };

  const handleCombinedDeliveryChange = (event) => {
    const checked = event.target.checked;
    setPairedUnitIds([]);
    setFormData(current => ({ ...current, allowCombinedDelivery: checked, teachingGroupCode: checked ? current.teachingGroupCode : '' }));
  };

  const handlePairUnitSelect = (unitIds) => {
    const selectedIds = Array.isArray(unitIds) ? unitIds : [];
    setPairedUnitIds(selectedIds);
    if (selectedIds.length === 0) {
      setFormData(current => ({ ...current, teachingGroupCode: '' }));
      setError(null);
      return;
    }
    const anchor = availableUnits.find(candidate => candidate.id === selectedIds[0]);
    if (!anchor) return;
    if (![anchor.creditHours, anchor.hoursPerWeek, anchor.sessionLengthMinutes].every(value => Number(value) > 0)) {
      setError('That unit has incomplete academic settings and cannot be used for grouping. Edit it first.');
      return;
    }
    const groupCode = anchor.teachingGroupCode || `GROUP-${String(anchor.code || anchor.id).replace(/[^A-Za-z0-9-]/g, '').toUpperCase()}`;
    setFormData(current => ({
      ...current,
      teachingGroupCode: groupCode,
      creditHours: Number(anchor.creditHours || 0),
      hoursPerWeek: Number(anchor.hoursPerWeek || 0),
      sessionLengthMinutes: Number(anchor.sessionLengthMinutes || 0),
      roomType: anchor.roomType || 'Standard Classroom'
    }));
    setError(null);
  };

  const groupedRoomTypes = roomTypes.reduce((groups, item) => {
    if (!groups[item.category]) groups[item.category] = [];
    groups[item.category].push(item);
    return groups;
  }, {});

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setLoading(true);
      setError(null);

      const parsedUnits = await parseUnitWorkbook(file);

      // Enrich with context
      const unitsToCreate = parsedUnits.map(u => {
        const program = programs.find(item => String(item.code).toLowerCase() === String(u.programCode).toLowerCase());
        if (!program) throw new Error(`Row ${u.rowNumber}: programme code ${u.programCode} was not found`);
        return { ...u, programId: program.id };
      });

      // Send to service
      const result = await unitService.createBulk(unitsToCreate);

      if (result.success) {
        setUploadStats({
          total: unitsToCreate.length,
          success: result.count
        });
        alert(`Successfully uploaded ${result.count} units!`);
        await onSave({ isBulk: true }); // Trigger refresh
        onClose();
      }

    } catch (err) {
      console.error('Bulk upload error:', err);
      setError('Failed to process file: ' + err.message);
    } finally {
      setLoading(false);
      // Reset input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleExport = async (scope) => {
    try {
      setLoading(true);
      // Fetch all units - ideally service supports filtering, but filtering client side for now as per established pattern
      const allUnits = await unitService.getAll();

      let filteredUnits = allUnits.filter(u => u.programId === context.programId);

      if (scope === 'module') {
        filteredUnits = filteredUnits.filter(u => u.module === context.module);
      }

      if (filteredUnits.length === 0) {
        alert('No units found to export.');
        setLoading(false);
        return;
      }

      const csvContent = unitsToCSV(filteredUnits);
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const programCode = programs.find(p => p.id === context.programId)?.code || 'prog';
      a.download = `units_${programCode}_${scope === 'module' ? `mod${context.module}` : 'all'}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export units');
    } finally {
      setLoading(false);
    }
  };

  // ---------------- RENDER STEPS ---------------- //

  // In renderStep1_Context:
  const renderStep1_Context = () => {
    const selectedProgram = programs.find(p => p.id === context.programId);
    const maxMod = selectedProgram && context.programLevel ? getMaxModules(selectedProgram, context.programLevel) : null;

    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Layers" size={32} className="text-primary" />
          </div>
          <h3 className="text-xl font-bold text-foreground">Select Context</h3>
          <p className="text-muted-foreground">Which program and module is this unit for?</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Program <span className="text-error">*</span>
            </label>
            <Select
              value={context.programId}
              onChange={(value) => {
                setContext({ ...context, programId: value, programLevel: '', module: 1 });
                setError(null);
              }}
              options={programs.map(program => ({value:program.id,label:`${program.code} — ${program.name}`}))}
              placeholder="Type to search programme name or code"
              searchable
              loading={loadingPrograms}
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Programme Level <span className="text-error">*</span></label>
            <select value={context.programLevel} onChange={(e) => setContext({ ...context, programLevel: e.target.value, module: 1 })} disabled={!selectedProgram} className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground" required>
              <option value="">Select a level</option>
              {(selectedProgram?.levels || []).map(level => <option key={level} value={level}>{String(level).replaceAll('_', ' ').replace(/\b\w/g, char => char.toUpperCase())}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Module Number <span className="text-error">*</span>
              {maxMod !== null && (
                <span className="text-xs text-muted-foreground ml-2">
                  (Max: {maxMod})
                </span>
              )}
            </label>
            <Input
              type="number"
              value={context.module}
              onChange={(e) => setContext({ ...context, module: parseInt(e.target.value) || 1 })}
              min="1"
              max={maxMod || 99}
              disabled={!context.programLevel}
              required
            />
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <Button onClick={handleContextSubmit} disabled={!context.programId || !context.programLevel}>
            Next Step <Icon name="ArrowRight" size={16} className="ml-2" />
          </Button>
        </div>
      </div>
    );
  };

  const renderStep2_Action = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Settings" size={32} className="text-primary" />
        </div>
        <h3 className="text-xl font-bold text-foreground">How do you want to add units?</h3>
        <p className="text-muted-foreground">
          {programs.find(p => p.id === context.programId)?.name} (Module {context.module})
        </p>
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-semibold text-slate-900">Excel bulk-upload template</p>
          <p className="text-sm text-slate-600">Download and complete the approved workbook before choosing Bulk Upload.</p>
        </div>
        <Button type="button" onClick={handleDownloadTemplate} className="shrink-0">
          <Icon name="Download" size={16} className="mr-2" /> Download Excel Template
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          onClick={() => handleActionSelect('single')}
          className="p-6 border border-border rounded-lg hover:border-primary hover:bg-primary/5 transition-all text-left flex flex-col items-start group"
        >
          <div className="bg-blue-100 p-3 rounded-full mb-4 group-hover:scale-110 transition-transform">
            <Icon name="Plus" size={24} className="text-blue-600" />
          </div>
          <h4 className="font-bold text-foreground text-lg mb-1">Enter Unit Details</h4>
          <p className="text-sm text-muted-foreground">Manually enter a single unit's details.</p>
        </button>

        <button
          onClick={() => handleActionSelect('bulk')}
          className="p-6 border border-border rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all text-left flex flex-col items-start group"
        >
          <div className="bg-purple-100 p-3 rounded-full mb-4 group-hover:scale-110 transition-transform">
            <Icon name="Upload" size={24} className="text-purple-600" />
          </div>
          <h4 className="font-bold text-foreground text-lg mb-1">Bulk Upload</h4>
          <p className="text-sm text-muted-foreground">Upload the completed Excel template with multiple units.</p>
        </button>
      </div>

      <div className="flex flex-col space-y-3 pt-4 border-t border-border mt-4">
        <div className="flex justify-between items-center">
          <Button variant="ghost" onClick={() => setStep(1)}>
            <Icon name="ArrowLeft" size={16} className="mr-2" /> Change Context
          </Button>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" onClick={() => handleExport('module')}>
              <Icon name="Download" size={14} className="mr-2" /> Export Module {context.module}
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport('program')}>
              <Icon name="Download" size={14} className="mr-2" /> Export All (Program)
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3_SingleForm = () => (
    <form onSubmit={handleSubmit} className="space-y-6">
      {!unit && (
        <div className="flex items-center justify-between bg-muted/50 p-3 rounded-md mb-4 text-sm">
          <span className="font-medium">
            Context: {programs.find(p => p.id === formData.programId)?.code} - Module {formData.module}
          </span>
          <button type="button" onClick={() => setStep(1)} className="text-primary hover:underline">Change</button>
        </div>
      )}

      {/* Basic Information */}
      <div className="space-y-4">
        <h4 className="font-medium text-foreground">Basic Information</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Unit Code <span className="text-error">*</span>
            </label>
            <Input
              name="code"
              value={formData?.code}
              onChange={handleChange}
              placeholder="e.g., CS101"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Unit Name <span className="text-error">*</span>
            </label>
            <Input
              name="name"
              value={formData?.name}
              onChange={handleChange}
              placeholder="e.g., Introduction to Programming"
              required
            />
          </div>
        </div>

        {/* Program Select - Hidden/Disabled if Context Set via Wizard, but visible if Edit or override needed */}
        <div className={(!unit && step === 3) ? 'hidden' : 'block'}>
          <label className="block text-sm font-medium text-foreground mb-2">Programme Level <span className="text-error">*</span></label>
          <select name="programLevel" value={formData.programLevel} onChange={handleChange} className="w-full px-3 py-2 mb-4 border border-border rounded-md bg-input text-foreground" required>
            <option value="">Select level</option>
            {(programs.find(program => program.id === formData.programId)?.levels || []).map(level => { const number = String(level).match(/[3-7]/)?.[0]; return <option key={level} value={number}>Level {number}</option>; })}
          </select>
          <label className="block text-sm font-medium text-foreground mb-2">
            Program <span className="text-error">*</span>
          </label>
          <select
            name="programId"
            value={formData?.programId}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            required
            disabled={loadingPrograms || (!unit && step === 3)}
          >
            <option value="">
              {loadingPrograms ? 'Loading programs...' : 'Select a program'}
            </option>
            {programs?.map(program => (
              <option key={program?.id} value={program?.id}>
                {program?.code} - {program?.name}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Unit Type <span className="text-error">*</span>
            </label>
            <select
              name="unitType"
              value={formData?.unitType}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              required
            >
              <option value="">Select Type</option>
              <option value="Core">Core</option>
              <option value="Basic">Basic</option>
              <option value="Common">Common</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Room Type <span className="text-error">*</span>
            </label>
            <select
              name="roomType"
              value={formData?.roomType}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={formData.allowCombinedDelivery && pairedUnitIds.length > 0}
              required
            >
              <option value="">Select Room Type</option>
              {Object.entries(groupedRoomTypes).map(([category, types]) => <optgroup key={category} label={category}>{types.map(type => <option key={type.id || type.name} value={type.name}>{type.name}</option>)}</optgroup>)}
            </select>
          </div>
        </div>

        <div className="rounded-lg border p-4 space-y-3">
          <label className="flex items-center gap-3"><input type="checkbox" name="allowCombinedDelivery" checked={formData.allowCombinedDelivery} onChange={handleCombinedDeliveryChange} /><span><b>Group with an existing unit</b><span className="block text-xs text-muted-foreground">Grouped units inherit one trainer, room requirement, timetable slots, credit hours, weekly hours and session duration.</span></span></label>
          {formData.allowCombinedDelivery && <>
            <Select label="Units to pair with" value={pairedUnitIds} onChange={handlePairUnitSelect} searchable clearable multiple required placeholder="Type a unit code or name..." options={availableUnits.map(candidate => { const complete = [candidate.creditHours, candidate.hoursPerWeek, candidate.sessionLengthMinutes].every(value => Number(value) > 0); const anchor = availableUnits.find(item => item.id === pairedUnitIds[0]); const selectedProgram = programs.find(item => item.id === formData.programId); const sameDepartment = !selectedProgram?.departmentId || candidate.program?.department_id === selectedProgram.departmentId; const differs = anchor && (Number(candidate.creditHours) !== Number(anchor.creditHours) || Number(candidate.hoursPerWeek) !== Number(anchor.hoursPerWeek) || Number(candidate.sessionLengthMinutes) !== Number(anchor.sessionLengthMinutes) || candidate.roomType !== anchor.roomType); return { value: candidate.id, label: `${candidate.code} — ${candidate.name}${differs ? ' (will inherit group settings)' : ''}`, description: `${candidate.program?.name || 'No programme'} · Level ${candidate.programLevel || '—'} · Module ${candidate.module || '—'}`, disabled: !sameDepartment || (!anchor && !complete) }; })} />
            {pairedUnitIds.length > 0 && <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800"><b>{pairedUnitIds.length} unit{pairedUnitIds.length === 1 ? '' : 's'} selected.</b> Inherited settings: {formData.creditHours} credit hours · {formData.hoursPerWeek} hours/week · {formData.sessionLengthMinutes} minutes/session · {formData.roomType}. Click a selected unit again or use × to remove pairing.</div>}
          </>}
        </div>

        <div className={(!unit && step === 3) ? 'hidden' : 'block'}>
          <label className="block text-sm font-medium text-foreground mb-2">
            Module Number <span className="text-error">*</span>
          </label>
          <Input
            type="number"
            name="module"
            value={formData?.module}
            onChange={handleChange}
            min="1"
            placeholder="e.g., 1"
            required
            disabled={!unit && step === 3}
          />
        </div>
      </div>

      {/* Academic Settings */}
      <div className="space-y-4">
        <h4 className="font-medium text-foreground">Academic Settings</h4>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Credit Hours
            </label>
            <Input
              type="number"
              name="creditHours"
              value={formData?.creditHours}
              onChange={handleChange}
              min="1"
              max="60"
              disabled={formData.allowCombinedDelivery && pairedUnitIds.length > 0}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Hours Per Week
            </label>
            <Input
              type="number"
              name="hoursPerWeek"
              value={formData?.hoursPerWeek}
              onChange={handleChange}
              min="1"
              max="20"
              disabled={formData.allowCombinedDelivery && pairedUnitIds.length > 0}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Session Length (min)
            </label>
            <Input
              type="number"
              name="sessionLengthMinutes"
              value={formData?.sessionLengthMinutes}
              onChange={handleChange}
              min="30"
              max="240"
              step="15"
              disabled={formData.allowCombinedDelivery && pairedUnitIds.length > 0}
            />
          </div>
        </div>
      </div>

      {/* Exam Settings */}
      <div className="space-y-4">
        <h4 className="font-medium text-foreground">Exam Settings</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Exam Theory Hours
            </label>
            <Input
              type="number"
              name="examTheoryHours"
              value={formData?.examTheoryHours || ''}
              onChange={handleChange}
              min="0"
              max="12"
              step="0.5"
              placeholder="e.g. 2.5"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Exam Practical Hours
            </label>
            <Input
              type="number"
              name="examPracticalHours"
              value={formData?.examPracticalHours || ''}
              onChange={handleChange}
              min="0"
              max="24"
              step="0.5"
              placeholder="e.g. 3"
            />
          </div>
        </div>
      </div>

      {/* Status Settings */}
      <div className="space-y-4">
        <h4 className="font-medium text-foreground">Status Settings</h4>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Total Enrolled Students
          </label>
          <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
            <div><p className="text-2xl font-bold text-blue-700">{Number(formData?.enrolled || 0).toLocaleString()}</p><p className="text-xs text-blue-700/70">Currently registered for this unit</p></div>
            <Icon name="Users" size={26} className="text-blue-600" />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            This counter is automatically updated based on student registrations.
          </p>
        </div>

        <div className="space-y-3">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              name="waitingListEnabled"
              checked={formData?.waitingListEnabled}
              onChange={handleChange}
              className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary"
            />
            <span className="text-sm text-foreground">
              Enable Waiting List
            </span>
          </label>

          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              name="isActive"
              checked={formData?.isActive}
              onChange={handleChange}
              className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary"
            />
            <span className="text-sm text-foreground">
              Active Unit
            </span>
          </label>
        </div>
      </div>
    </form>
  )

  const renderStep3_BulkUpload = () => (
    <div className="space-y-6 text-center py-8">
      <input
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ref={fileInputRef}
        onChange={handleFileUpload}
        className="hidden"
      />

      <div
        className="bg-muted border-2 border-dashed border-border rounded-lg p-10 cursor-pointer hover:bg-muted/80 transition-colors"
        onClick={() => fileInputRef.current?.click()}
      >
        <Icon name="UploadCloud" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h4 className="text-lg font-medium text-foreground">Click to Upload Excel Workbook</h4>
        <p className="text-muted-foreground text-sm mt-2">or drag and drop here</p>
      </div>

      <div className="flex justify-center space-x-4">
        <Button variant="outline" onClick={handleDownloadTemplate}>
          <Icon name="Download" size={16} className="mr-2" /> Download Template
        </Button>
      </div>

      <div className="text-left bg-blue-50 p-4 rounded-md text-sm text-blue-800">
        <p><strong>Note:</strong> Uploading will add units to:</p>
        <ul className="list-disc list-inside mt-1">
          <li>Program: {programs.find(p => p.id === context.programId)?.name}</li>
          <li>Module: {context.module}</li>
        </ul>
      </div>

      <div className="flex justify-between pt-4 border-t border-border mt-4">
        <Button variant="outline" onClick={() => setStep(2)}>
          <Icon name="ArrowLeft" size={16} className="mr-2" /> Back
        </Button>
      </div>
    </div>
  )


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h3 className="text-xl font-bold text-foreground">
              {unit ? 'Edit Unit' : (step === 1 ? 'New Unit Context' : step === 2 ? 'Choose Action' : (actionType === 'bulk' ? 'Bulk Upload Units' : 'Create New Unit'))}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              {unit ? 'Update unit information' : 'Add academic units to program structure'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            disabled={loading}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-error/10 border border-error rounded-lg p-4 mb-4 flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-error">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="text-error hover:text-error/80"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
          )}

          {step === 1 && renderStep1_Context()}
          {step === 2 && renderStep2_Action()}
          {step === 3 && actionType === 'single' && renderStep3_SingleForm()}
          {step === 3 && actionType === 'bulk' && renderStep3_BulkUpload()}

        </div>

        {/* Footer (Only for Single Form Step 3) */}
        {step === 3 && actionType === 'single' && (
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
            {/* Back button for Wizard flow */}
            {!unit && (
              <Button
                variant="ghost"
                onClick={() => setStep(2)}
                disabled={loading}
                className="mr-auto"
              >
                <Icon name="ArrowLeft" size={16} className="mr-2" /> Back
              </Button>
            )}

            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} className="mr-2" />
                  {unit ? 'Update Unit' : 'Create Unit'}
                </>
              )}
            </Button>
          </div>
        )}
      </div >
    </div >
  );
};

export default UnitModal;
